/**
 * Kusto (Azure Data Explorer / KQL) Script Provider
 *
 * Kusto is an append-only analytics engine with no DDL/DML surface comparable
 * to the SQL engines, so this provider implements exactly ONE thing the product
 * can honestly generate: "Select top 1000 rows" as the KQL `Table | take 1000`.
 *
 * Every other ScriptProvider capability (CREATE/DROP/INSERT/UPDATE/DELETE,
 * database scripts, "Script New" templates) has no correct KQL rendering, so it
 * throws a clear error rather than emitting SQL a Kusto cluster would reject.
 * The Object Explorer already gates these actions off for kusto nodes; the
 * throws are the second line of defence for any path that reaches the provider.
 */

import type {
  ScriptProvider,
  TableScriptType,
  TableScriptContext,
  DatabaseScriptType,
} from '../types/scriptProvider';
import { SELECT_TOP_ROW_LIMIT } from '../selectTopRowLimit';

const UNSUPPORTED = 'Kusto (KQL) does not support this operation.';

/**
 * Quote a Kusto identifier. Simple identifiers pass through bare; anything else
 * gets `['name']` bracket quoting whose inner content is a non-verbatim KQL
 * string literal (C-style backslash escapes — backslash first so a trailing `\`
 * can't escape the closing quote). Matches `quoteKustoName` in
 * `database-objects/services/queries/kustoQueries.ts`.
 */
function quoteKustoIdentifier(name: string): string {
  if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) return name;
  const escaped = name.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  return `['${escaped}']`;
}

export const kustoScriptProvider: ScriptProvider = {
  engine: 'kusto',

  quoteIdentifier: quoteKustoIdentifier,

  // Kusto has no schema concept — tables are addressed by name only.
  qualifyTableName(_schema: string, table: string): string {
    return quoteKustoIdentifier(table);
  },

  generateTableScript(type: TableScriptType, ctx: TableScriptContext): string {
    if (type === 'select-top') {
      return `${quoteKustoIdentifier(ctx.tableName)} | take ${SELECT_TOP_ROW_LIMIT}`;
    }
    throw new Error(`Kusto (KQL) cannot generate a "${type}" table script.`);
  },

  generateDatabaseScript(_type: DatabaseScriptType): string {
    throw new Error(UNSUPPORTED);
  },

  generateNewTableTemplate(): string {
    throw new Error(UNSUPPORTED);
  },

  generateNewViewTemplate(): string {
    throw new Error(UNSUPPORTED);
  },

  generateNewProcedureTemplate(): string {
    throw new Error(UNSUPPORTED);
  },

  generateNewFunctionTemplate(): string {
    throw new Error(UNSUPPORTED);
  },

  generateNewDatabaseTemplate(): string {
    throw new Error(UNSUPPORTED);
  },
};
